import React, { useState, useEffect, useCallback } from "react";
import CoachMembershipSkeleton from "../../components/layout/CoachMembershipSkeleton";
import { createPortal } from "react-dom";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, Crown, Clock, XCircle, FileText, Shield, ScrollText, ArrowUpRight } from "lucide-react";

export default function CoachMembership() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [membership, setMembership] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchMembership = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get("/coach/my-membership");
            setMembership(res.data.membership || null);
        } catch {
            setMembership(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMembership(); }, [fetchMembership]);

    const showMsg = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 5000);
    };

    // Returning from Stripe checkout: verify the session, then clean the URL.
    useEffect(() => {
        const sessionId = searchParams.get("session_id");
        if (!sessionId) return;
        (async () => {
            try {
                const res = await api.post("/coach/verify-session", { session_id: sessionId });
                setMembership(res.data.membership || null);
                showMsg(res.data.message || "Membership activated.", "success");
            } catch (err) {
                showMsg(err.response?.data?.error || "Could not verify your payment.", "error");
            } finally {
                setSearchParams({}, { replace: true });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCancelMembership = async () => {
        setCancelling(true);
        try {
            await api.post("/coach/cancel-membership");
            setShowCancelConfirm(false);
            await fetchMembership();
            showMsg("Membership cancelled.", "success");
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to cancel membership.", "error");
        } finally {
            setCancelling(false);
        }
    };

    if (loading) {
        return <CoachMembershipSkeleton isDarkMode={isDarkMode} />;
    }

    return (
        <div className={`max-w-2xl mx-auto space-y-6 py-10 px-4 ${isDarkMode ? "bg-slate-900" : ""}`}>
            <header className="text-center">
                <h1 className={`text-2xl font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>My Membership</h1>
                <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Manage your subscription and billing</p>
            </header>

            {message && (
                <div role="alert" className={`flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                    message.type === "error"
                        ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                        : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                }`}>{message.text}</div>
            )}

            {/* Plan Card */}
            <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                <div className="bg-slate-900 px-6 py-6 text-center relative">
                    <Crown className="text-amber-400 mx-auto mb-2" size={28} />
                    <h2 className="text-xl font-bold text-white">{membership?.TierName || "Free coach account"}</h2>
                    <span className={`inline-block mt-2 px-3 py-0.5 rounded-full text-xs font-bold border ${
                        membership?.CancelledAt
                            ? "bg-amber-500/20 text-amber-400 border-amber-500/30"
                            : (membership?.isActive || !membership)
                                ? "bg-primary-500/20 text-primary-400 border-primary-500/30"
                                : "bg-red-500/20 text-red-400 border-red-500/30"
                    }`}>
                        {membership?.CancelledAt ? "Cancelled" : (membership?.isActive || !membership) ? "Active" : "Inactive"}
                    </span>
                    {membership?.Price ? (
                        <p className="mt-3">
                            <span className="text-3xl font-extrabold text-primary-400">${Number(membership.Price).toFixed(2)}</span>
                            <span className="text-sm text-slate-400">/mo</span>
                        </p>
                    ) : null}
                </div>

                <div className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        <div className={`rounded-xl p-3 ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Calendar className="text-primary-500" size={14} />
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>Started</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? "text-slate-200" : "text-slate-900"}`}>
                                {membership?.StartDate ? new Date(membership.StartDate).toLocaleDateString() : "N/A"}
                            </p>
                        </div>
                        <div className={`rounded-xl p-3 ${isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}`}>
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="text-primary-500" size={14} />
                                <p className={`text-[11px] font-semibold uppercase tracking-wide ${isDarkMode ? "text-slate-400" : "text-slate-400"}`}>{membership?.CancelledAt ? "Expires" : "Renews"}</p>
                            </div>
                            <p className={`text-sm font-bold ${isDarkMode ? "text-slate-200" : "text-slate-900"}`}>
                                {membership?.EndDate ? new Date(membership.EndDate).toLocaleDateString() : "Ongoing"}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <button
                            className="w-full py-3 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 bg-primary-500 text-white hover:bg-primary-600 shadow-sm"
                            onClick={() => navigate("/coaching/membership/plans")}
                        >
                            <ArrowUpRight size={16} /> {membership?.isActive ? "View Plans" : "Upgrade Now"}
                        </button>
                        {membership?.isActive && !membership?.CancelledAt && Number(membership?.Price) > 0 && (
                            <button
                                onClick={() => setShowCancelConfirm(true)}
                                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors flex items-center justify-center gap-2 border ${
                                    isDarkMode ? "text-red-400 border-red-800/50 hover:bg-red-900/20" : "text-red-500 border-red-200 hover:bg-red-50"
                                }`}
                            >
                                <XCircle size={16} /> Cancel Membership
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className={`flex flex-wrap justify-center gap-5 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                <a href="/membership-agreement" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><ScrollText size={12} /> Membership Agreement</a>
                <a href="/terms-of-service" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><FileText size={12} /> Terms of Service</a>
                <a href="/privacy-policy" className="flex items-center gap-1 hover:text-slate-600 transition-colors"><Shield size={12} /> Privacy Policy</a>
            </div>

            {showCancelConfirm && createPortal(
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ zIndex: 9999 }} onClick={() => setShowCancelConfirm(false)}>
                    <div className={`rounded-2xl shadow-xl max-w-md w-full mx-4 p-6 ${isDarkMode ? "bg-slate-800" : "bg-white"}`} onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 rounded-full"><XCircle className="text-red-500" size={24} /></div>
                            <h3 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Cancel Membership</h3>
                        </div>
                        <p className={`text-sm mb-6 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                            Are you sure you want to cancel your membership? You'll lose access to premium coaching features.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowCancelConfirm(false)}
                                className={`flex-1 py-3 rounded-xl font-semibold border transition-colors ${isDarkMode ? "border-slate-600 text-slate-300 hover:bg-slate-700" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}
                            >
                                Keep Membership
                            </button>
                            <button
                                onClick={handleCancelMembership}
                                disabled={cancelling}
                                className="flex-1 py-3 rounded-xl font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                                {cancelling ? "Cancelling..." : "Yes, Cancel"}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
